import { ColourPicker } from '@components/core-ui/ColourPicker';
import { AutoCompleteSelect } from '@components/core-ui/form-elements/AutocompleteSelect';
import { CheckBox } from '@components/core-ui/form-elements/CheckBox';
import { CurrencyInput } from '@components/core-ui/form-elements/CurrencyInput';
import { DatePicker } from '@components/core-ui/form-elements/DatePicker';
import { Input } from '@components/core-ui/form-elements/Input';
import { NumericInput } from '@components/core-ui/form-elements/NumericInput';
import { PhoneInput } from '@components/core-ui/form-elements/PhoneInput';
import { Radio } from '@components/core-ui/form-elements/Radio';
import { Select } from '@components/core-ui/form-elements/Select';
import { Switch } from '@components/core-ui/form-elements/Switch';
import { Textarea } from '@components/core-ui/form-elements/Textarea';
import { TextDatePicker } from '@components/core-ui/form-elements/TextDatePicker';
import { TimeInput } from '@components/core-ui/form-elements/TimeInput';
import { TimeZoneSelect } from '@components/core-ui/form-elements/TimeZoneSelect';
import { showcasePagePanelClassName } from '@pages/Admin/ComponentShowcasePage/helpers/showcasePagePanelClassName';
import { showcaseSurfacePanelClassName } from '@pages/Admin/ComponentShowcasePage/helpers/showcaseSurfacePanelClassName';
import { ReactNode, useState } from 'react';

const componentLabelClassName = 'text-xs font-semibold uppercase tracking-wide text-foreground-muted';
const variantLabelClassName = 'mb-2 text-xs font-medium text-foreground-muted';

const pairColumnClassName = 'flex max-w-md flex-col gap-2';

const iconLeftDatePickerClassName =
  'form-input w-full [&_.icon]:!left-[15px] [&_.icon]:!right-auto [&_input]:!pl-10 [&_input]:!pr-4';

const selectOptions = [
  { value: 'one', label: 'One' },
  { value: 'two', label: 'Two' },
];

const autocompleteOptions = [
  { value: 'alpha', label: 'Alpha' },
  { value: 'beta', label: 'Beta' },
];

type TFormShowcasePairProps = {
  title: string;
  children: ReactNode;
};

const FormShowcasePair = ({ title, children }: TFormShowcasePairProps) => (
  <div className="flex flex-col gap-2">
    <p className={componentLabelClassName}>{title}</p>
    <div className={pairColumnClassName}>{children}</div>
  </div>
);

type TFormSurfaceShowcaseProps = {
  title: string;
  children: ReactNode;
};

const FormSurfaceShowcase = ({ title, children }: TFormSurfaceShowcaseProps) => (
  <div className="flex flex-col gap-2">
    <p className={componentLabelClassName}>{title}</p>
    <div className="flex flex-col gap-8">
      <div>
        <p className={variantLabelClassName}>On page background</p>
        <div className={[showcasePagePanelClassName, pairColumnClassName].join(' ')}>{children}</div>
      </div>
      <div>
        <p className={variantLabelClassName}>On surface (modal)</p>
        <div className={[showcaseSurfacePanelClassName, pairColumnClassName].join(' ')}>{children}</div>
      </div>
    </div>
  </div>
);

const InputShowcaseRows = () => (
  <>
    <Input name="input" label="Input" value="Sample" onChange={() => undefined} className="form-input w-full" />
    <Input
      name="input-disabled"
      label="Input"
      value="Sample"
      onChange={() => undefined}
      disabled
      className="form-input w-full"
    />
  </>
);

const SelectShowcaseRows = ({
  selectValue,
  onSelectChange,
}: {
  selectValue: string;
  onSelectChange: (name: string, value: string) => void;
}) => (
  <>
    <Select
      name="select"
      label="Select"
      value={selectValue}
      onChange={onSelectChange}
      options={selectOptions}
      className="form-select w-full"
    />
    <Select
      name="select-disabled"
      label="Select"
      value="one"
      onChange={() => undefined}
      options={selectOptions}
      disabled
      className="form-select w-full"
    />
  </>
);

const TextareaShowcaseRows = () => (
  <>
    <Textarea
      name="textarea"
      label="Textarea"
      value="Sample text"
      onChange={() => undefined}
      className="textarea-wrapper w-full"
    />
    <Textarea
      name="textarea-disabled"
      label="Textarea"
      value="Sample text"
      onChange={() => undefined}
      disabled
      className="textarea-wrapper w-full"
    />
  </>
);

export const FormsDemo = () => {
  const [checked, setChecked] = useState(false);
  const [switchChecked, setSwitchChecked] = useState(false);
  const [radioValue, setRadioValue] = useState('one');
  const [selectValue, setSelectValue] = useState('one');
  const [autocompleteValue, setAutocompleteValue] = useState('alpha');
  const [colour, setColour] = useState('#40608f');
  const [date, setDate] = useState<Date | null>(new Date());
  const [textDate, setTextDate] = useState('06/02/2026');
  const [deadlineDate, setDeadlineDate] = useState('06/02/2026');
  const [phone, setPhone] = useState('+1 555 0100');
  const [currency, setCurrency] = useState('10');
  const [numeric, setNumeric] = useState(5);
  const [time, setTime] = useState('09:30');
  const [timezone, setTimezone] = useState('America/New_York');

  return (
    <div className={[showcasePagePanelClassName, 'flex max-w-md flex-col gap-8'].join(' ')}>
      <FormSurfaceShowcase title="Input">
        <InputShowcaseRows />
      </FormSurfaceShowcase>

      <FormSurfaceShowcase title="Select">
        <SelectShowcaseRows
          selectValue={selectValue}
          onSelectChange={(_name, value) => setSelectValue(String(value))}
        />
      </FormSurfaceShowcase>

      <FormSurfaceShowcase title="Textarea">
        <TextareaShowcaseRows />
      </FormSurfaceShowcase>

      <FormShowcasePair title="Checkbox">
        <CheckBox name="checkbox" label="Checkbox label" checked={checked} onChange={setChecked} />
        <CheckBox
          name="checkbox-disabled"
          label="Checkbox (disabled)"
          checked={false}
          disabled
          onChange={() => undefined}
        />
        <CheckBox
          name="checkbox-disabled-checked"
          label="Checkbox (disabled, selected)"
          checked
          disabled
          onChange={() => undefined}
        />
      </FormShowcasePair>

      <FormShowcasePair title="Radio">
        <Radio name="radio" label="Option one" checked={radioValue === 'one'} onChange={() => setRadioValue('one')} />
        <Radio name="radio" label="Option two" checked={radioValue === 'two'} onChange={() => setRadioValue('two')} />
        <Radio
          name="radio-disabled"
          label="Option one (disabled, selected)"
          checked
          disabled
          onChange={() => undefined}
        />
        <Radio
          name="radio-disabled"
          label="Option two (disabled)"
          checked={false}
          disabled
          onChange={() => undefined}
        />
      </FormShowcasePair>

      <FormShowcasePair title="Switch">
        <Switch label="Switch label" checked={switchChecked} onChange={() => setSwitchChecked(!switchChecked)} />
        <Switch label="Switch (disabled)" checked={false} disabled onChange={() => undefined} />
        <Switch label="Switch (disabled, on)" checked disabled onChange={() => undefined} />
      </FormShowcasePair>

      <FormShowcasePair title="TextDatePicker">
        <TextDatePicker label="Text date" value={textDate} onChange={setTextDate} className="form-input w-full" />
        <TextDatePicker
          label="Text date"
          value="06/02/2026"
          onChange={() => undefined}
          disabled
          className="form-input w-full"
        />
      </FormShowcasePair>

      <FormShowcasePair title="TextDatePicker (icon left)">
        <TextDatePicker
          label="Deadline"
          value={deadlineDate}
          onChange={setDeadlineDate}
          className={iconLeftDatePickerClassName}
        />
        <TextDatePicker
          label="Deadline"
          value="06/02/2026"
          onChange={() => undefined}
          disabled
          className={iconLeftDatePickerClassName}
        />
      </FormShowcasePair>

      <FormShowcasePair title="DatePicker">
        <DatePicker label="Date" value={date} onChange={setDate} className="form-input w-full" />
        <DatePicker label="Date" value={new Date()} onChange={() => undefined} disabled className="form-input w-full" />
      </FormShowcasePair>

      <FormShowcasePair title="PhoneInput">
        <PhoneInput label="Phone" value={phone} onChange={setPhone} className="form-input w-full" />
        <PhoneInput
          label="Phone"
          value="+1 555 0100"
          onChange={() => undefined}
          disabled
          className="form-input w-full"
        />
      </FormShowcasePair>

      <FormShowcasePair title="CurrencyInput">
        <CurrencyInput
          name="currency"
          label="Price"
          value={currency}
          onChange={(_value, formatted) => setCurrency(formatted)}
          className="form-input w-full"
        />
        <CurrencyInput
          name="currency-disabled"
          label="Price"
          value="10"
          onChange={() => undefined}
          disabled
          className="form-input w-full"
        />
      </FormShowcasePair>

      <FormShowcasePair title="NumericInput">
        <NumericInput label="Quantity" value={numeric} onChange={setNumeric} min={0} max={10} />
        <NumericInput label="Quantity" value={5} onChange={() => undefined} min={0} max={10} disabled />
      </FormShowcasePair>

      <FormShowcasePair title="AutoCompleteSelect">
        <AutoCompleteSelect
          name="autocomplete"
          label="AutoCompleteSelect"
          value={autocompleteValue}
          onChange={(_name, value) => setAutocompleteValue(String(value))}
          options={autocompleteOptions}
          className="form-autocomplete w-full"
        />
        <AutoCompleteSelect
          name="autocomplete-disabled"
          label="AutoCompleteSelect"
          value="alpha"
          onChange={() => undefined}
          options={autocompleteOptions}
          disabled
          className="form-autocomplete w-full"
        />
      </FormShowcasePair>

      <FormShowcasePair title="TimeInput">
        <TimeInput label="TimeInput" value={time} onChange={setTime} className="form-time-input w-full" />
        <TimeInput
          label="TimeInput"
          value="09:30"
          onChange={() => undefined}
          disabled
          className="form-time-input w-full"
        />
      </FormShowcasePair>

      <FormShowcasePair title="TimeZoneSelect">
        <TimeZoneSelect
          name="timezone"
          label="TimeZoneSelect"
          value={timezone}
          onChange={(_name, value) => setTimezone(String(value))}
          className="form-autocomplete w-full"
        />
        <TimeZoneSelect
          name="timezone-disabled"
          label="TimeZoneSelect"
          value="America/New_York"
          onChange={() => undefined}
          disabled
          className="form-autocomplete w-full"
        />
      </FormShowcasePair>

      <FormShowcasePair title="ColourPicker">
        <ColourPicker colour={colour} setColour={setColour} />
        <ColourPicker colour="#40608f" setColour={() => undefined} disabled />
      </FormShowcasePair>
    </div>
  );
};
